import { cn } from "@/lib/utils";
import { Card, Label, MonoMeta, StateWord } from "@/design/primitives";
import { BrandIcon } from "@/design/primitives";

// ProjectCard — used by the workshop and the recovery lanes. Council round
// 2026-10-21 council round: bookkeeping left the card — the phase and
// Kit's latest line are what a desk shows.

const STATE_WORD = {
  INTAKE: { word: "New — needs shaping", tone: "muted" },
  PLAN: { word: "Taking shape", tone: "amber" },
  EXECUTE: { word: "Being worked on", tone: "green" },
  TEST: { word: "Being verified", tone: "amber" },
  CLOSE: { word: "Wrapping up", tone: "chip" },
  ARCHIVED: { word: "Parked or done", tone: "dimmed" },
  TABLED: { word: "Parked for now", tone: "muted" },
  UNSORTED: { word: "Needs sorting", tone: "amber" },
};

export const ProjectHeading = ({ card, as: Title = "h3", className }) => (
  <div className={className}>
    <div className="flex items-baseline justify-between gap-3">
      <Title className="min-w-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-os-ink">
        {card.title}
      </Title>
      <StateWord tone={STATE_WORD[card.rawState]?.tone || "muted"}>
        {STATE_WORD[card.rawState]?.word || card.stateWord}
      </StateWord>
    </div>
    <div className="mt-1 flex items-center gap-1.5">
      <BrandIcon brand={card.brand} size={18} />
      <Label as="p" data-testid="project-card-brand">
        {card.brand}
      </Label>
    </div>
  </div>
);

export const ProjectNext = ({ card, className }) => (
  <div className={cn("mt-3 flex flex-col gap-2", className)}>
    <p data-testid="project-card-next" className="text-[0.875rem] font-medium leading-[1.45] text-os-ink">
      {card.next}
    </p>
    <p
      data-testid="project-card-blockers"
      className={cn(
        "text-[0.8125rem] leading-[1.4]",
        (card.blockersLine || "").startsWith("Waiting on:") ? "text-os-amber" : "text-os-ink-3",
      )}
    >
      {card.blockersLine}
    </p>
    {card.needsDusting && (
      <p data-testid="project-card-dusting" className="text-[0.8125rem] font-medium text-os-amber">
        Needs dusting — hasn't moved in a while
      </p>
    )}
  </div>
);

export const ProjectCard = ({ card, onOpen }) => {
  const open = () => onOpen(card.slug);
  const archived = card.rawState === "ARCHIVED" || card.rawState === "TABLED";
  const runner = card.runner || null;
  const phases = runner?.phases || [];
  const doneCount = phases.filter((x) => x.status === "done").length;
  const phaseLine = phases.length > 0
    ? `Phase ${Math.min(doneCount + 1, phases.length)} of ${phases.length}`
    : card.phaseCount
      ? `${card.phaseCount} planned steps`
      : "";
  const lastUpdate = phases.filter((x) => x.update).slice(-1)[0]?.update || card.lastKitAction;
  return (
    <Card
      role="button"
      tabIndex={0}
      quiet={archived || card.rawState === "INTAKE"}
      data-testid={`project-card-${card.slug}`}
      data-state={card.rawState}
      aria-label={`${card.title}, ${card.brand}. Open details`}
      onClick={open}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), open())}
      className={cn(
        "flex h-full cursor-pointer flex-col p-5 text-left hover:border-os-border-strong focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-os-ink",
        archived && "opacity-70",
      )}
    >
      <ProjectHeading card={card} />
      <ProjectNext card={card} className="mt-3 flex-1" />
      <div className="mt-4 border-t border-os-border pt-3">
        {phaseLine && <MonoMeta as="p" data-testid="project-card-phase">{phaseLine}</MonoMeta>}
        {lastUpdate && (
          <p data-testid="project-card-update" className="mt-1 line-clamp-2 text-[0.8125rem] leading-[1.45] text-os-ink-3">
            {lastUpdate}
          </p>
        )}
      </div>
    </Card>
  );
};